import type { BodyNodeLink, Node } from '../types';

export interface JointDefinition {
  key: JointKey;
  label: string;
  a: number;
  b: number;
  c: number;
  color: string;
}

export type JointKey =
  | 'leftElbow'
  | 'rightElbow'
  | 'leftShoulder'
  | 'rightShoulder'
  | 'leftHip'
  | 'rightHip'
  | 'leftKnee'
  | 'rightKnee'
  | 'leftAnkle'
  | 'rightAnkle'
  | 'leftWrist'
  | 'rightWrist'
  | 'leftFoot'
  | 'rightFoot';

export type JointAngles = Partial<Record<JointKey, number>>;

export const JOINT_DEFINITIONS: readonly JointDefinition[] = [
  { key: 'leftElbow', label: 'L Elbow', a: 11, b: 13, c: 15, color: '#62d9ff' },
  { key: 'rightElbow', label: 'R Elbow', a: 12, b: 14, c: 16, color: '#ff5477' },
  { key: 'leftShoulder', label: 'L Shoulder', a: 13, b: 11, c: 23, color: '#7affa0' },
  { key: 'rightShoulder', label: 'R Shoulder', a: 14, b: 12, c: 24, color: '#ffb347' },
  { key: 'leftHip', label: 'L Hip', a: 11, b: 23, c: 25, color: '#c080ff' },
  { key: 'rightHip', label: 'R Hip', a: 12, b: 24, c: 26, color: '#ffd966' },
  { key: 'leftKnee', label: 'L Knee', a: 23, b: 25, c: 27, color: '#5ee0c9' },
  { key: 'rightKnee', label: 'R Knee', a: 24, b: 26, c: 28, color: '#ff77d9' },
  { key: 'leftAnkle', label: 'L Ankle', a: 25, b: 27, c: 31, color: '#4dc6ff' },
  { key: 'rightAnkle', label: 'R Ankle', a: 26, b: 28, c: 32, color: '#ff9a6b' },
  { key: 'leftWrist', label: 'L Wrist', a: 13, b: 15, c: 19, color: '#a7ffcf' },
  { key: 'rightWrist', label: 'R Wrist', a: 14, b: 16, c: 20, color: '#ffa7cf' },
  // Foot arch / dorsiflexion: heel (29/30) → ankle (27/28) → foot_index (31/32)
  { key: 'leftFoot', label: 'L Foot', a: 29, b: 27, c: 31, color: '#9adcff' },
  { key: 'rightFoot', label: 'R Foot', a: 30, b: 28, c: 32, color: '#ff9aa2' },
];

export function angleBetween(a: Node, b: Node, c: Node): number {
  const vx1 = a.x - b.x;
  const vy1 = a.y - b.y;
  const vx2 = c.x - b.x;
  const vy2 = c.y - b.y;
  const mag1 = Math.hypot(vx1, vy1);
  const mag2 = Math.hypot(vx2, vy2);
  if (mag1 === 0 || mag2 === 0) return Number.NaN;
  const cos = (vx1 * vx2 + vy1 * vy2) / (mag1 * mag2);
  const clamped = Math.max(-1, Math.min(1, cos));
  return (Math.acos(clamped) * 180) / Math.PI;
}

export function computeJointAngles(
  bnl: BodyNodeLink,
  minScore: number,
  definitions: readonly JointDefinition[] = JOINT_DEFINITIONS,
): JointAngles {
  const out: JointAngles = {};
  for (const def of definitions) {
    const a = bnl.nodes[def.a];
    const b = bnl.nodes[def.b];
    const c = bnl.nodes[def.c];
    if (!a || !b || !c) continue;
    if (a.score < minScore || b.score < minScore || c.score < minScore) continue;
    const angle = angleBetween(a, b, c);
    if (!Number.isFinite(angle)) continue;
    out[def.key] = angle;
  }
  return out;
}
